import { useEffect, useState, useRef } from 'react';
import { Redirect } from 'react-router-dom';

import {
	Flex, Box, Divider, Image, Tabs, TabList, TabPanels, Tab, TabPanel, useToast, PopoverContent,
	Popover, PopoverTrigger, PopoverHeader, PopoverCloseButton, PopoverBody, PopoverFooter, Icon, Avatar
} from '@chakra-ui/react';
import { RiVerifiedBadgeFill } from "react-icons/ri";
import '../styles/user-profile.scss'
import LaunchdeckModal from '../../global/components/helper-components/components/LaunchdeckModal';
import UserEditPersonalInfoForm from './UserEditPersonalInfoForm';
import OrgManagementModule from './manage-organization/OrgManagementModule';
import UserProfileImageDisplay from '../../global/components/helper-components/components/UserProfileImageDisplay';
import { determineIsNonCustomerUserAccount, getFullName } from '../helpers/user-helper';
import OrgUpCtaModule from './OrgUpCtaModule';
import UserProfileMobileNavMenu from './UserProfileMobileNavMenu';
import LaunchdeckButton from '../../global/components/helper-components/components/LaunchdeckButton';
import { triggerLaunchdeckToast } from '../../global/helpers/global-helpers';
import OrgBillingModule from './manage-organization/OrgBillingModule';
import LaunchdeckInteractiveButton from '../../global/components/helper-components/components/LaunchdeckInteractiveButton';
import LoggedOutConfirmationModal from '../../digital-sales-room/dsr-general-mgmt/LoggedOutConfirmationModal';
import { MdOutlineTipsAndUpdates } from 'react-icons/md';
import ExternalIntegrationsModule from './manage-user/ExternalIntegrationsModule';
import VerifiedUserTag from '../../global/components/product-marketing/VerifiedUserTag';
import LaunchdeckSpinner from '../../global/components/helper-components/components/LaunchdeckSpinner';
import UserSettingsManagementModule from './manage-user/UserSettingsManagementModule';
import PageLoader from '../../global/components/helper-components/components/PageLoader';
import LaunchdeckTooltip from '../../global/components/helper-components/components/LaunchdeckTooltip';


export default function UserProfilePage({
	userAccountProfile,
	paidFeatureAccess,
	userProfilePicture,
	resendEmailVerification,
	getUserProfile,
	adminOrgProfile,
	manageAdminOrgUsersOutcome,
	editProfileOutcome,
	isMobileView,
	updateOrgProfile,
	updateOrgProfileReset,
	isOrgAdmin
}) {

	const [localState, setLocalState] = useState({
		pageIsLoading: true,
		isSetupMode: false,
		firstName: "",
		lastName: "",
		fullName: "",
		email: "",
		meetingLink: "",
		phoneCountryCode: "",
		phoneAreaCode: "",
		phoneNumber: "",
		profilePicture: "",
		orgLogo: "",
		isVerified: "",
		profileIsEmpty: "",
		isAdministrator: null,
		isNoOrgUser: null,
		userOrgTitle: "",
		// Profile Image management
		uploadedPicture: ""
	})
	// UX handling
	const [showEditProfileModal, setShowEditProfileModal] = useState(false)
	const [hideVerifyEmailPopover, setHideVerifyEmailPopover] = useState(false)
	const [displayedSectionIndex, setDisplayedSectionIndex] = useState(0)

	// Manage Query
	const queryString = window.location.search;
	const urlParams = new URLSearchParams(queryString);
	const sectionIndex = urlParams.get('section');

	const initRef = useRef(false);
	const toast = useToast();
	const [mobilePageLoading, setMobilePageLoading] = useState(false)

	// Fetch data
	useEffect(() => {
		if (getUserProfile && !initRef.current) {
			getUserProfile({
				getUserInfo: true,
				getContactDetails: true,
				getUserOrgInfo: true,
				getIntegrationStatuses: true
			})

			initRef.current = true
		}
	}, [getUserProfile])

	// Initialize obtained info
	useEffect(() => {
		const initializeState = (userAccountProfile) => {
			const isNoOrgUser = determineIsNonCustomerUserAccount();

			const { email, firstName, lastName, meetingLink, contactPhoneNumber, phoneCountryCode, phoneAreaCode, phoneNumber, isVerified } = userAccountProfile || {};
			const { orgName, userOrgTitle, orgLogo, orgBranding } = userAccountProfile || {};
			const fullName = getFullName(userAccountProfile);
			const profileIsEmpty = firstName ? true : false;

			setLocalState(prevState => ({
				...prevState,
				firstName,
				lastName,
				fullName,
				email,
				meetingLink,
				phoneCountryCode,
				phoneAreaCode,
				phoneNumber,
				profilePicture: userProfilePicture || "",
				pageIsLoading: false,
				isVerified,
				profileIsEmpty,
				isAdministrator: isOrgAdmin,
				isNoOrgUser,
				userOrgTitle,
				contactPhoneNumber,
				orgName,
				orgLogo,
				...orgBranding && { orgBranding }
			}));
		}

		if (userAccountProfile) {
			initializeState(userAccountProfile)
		}
	}, [userAccountProfile, isOrgAdmin])

	useEffect(() => {
		if (sectionIndex) {
			if (Number.isInteger(parseInt(sectionIndex))) {
				setDisplayedSectionIndex(parseInt(sectionIndex))
			} else {
				const { isAdministrator } = localState || {}
				translateTextSectionIndex(sectionIndex, isAdministrator)
			}
		}
	}, [sectionIndex, localState])

	useEffect(() => {
		if (editProfileOutcome) {
			const { updatedUser: updatedProfile } = editProfileOutcome;
			const { email, firstName, lastName, meetingLink, phoneCountryCode, phoneAreaCode, phoneNumber, profilePicture, isVerified } = updatedProfile || {};

			setLocalState(prevState => ({
				...prevState,
				firstName,
				lastName,
				fullName: firstName + ' ' + lastName,
				email,
				meetingLink,
				phoneCountryCode,
				phoneAreaCode,
				phoneNumber,
				profilePicture,
				isVerified
			}));

			toast(
				triggerLaunchdeckToast({
					useCase: "show-success-state",
					label: "Profile updated successfully",
					isMobileView
				})
			)
		}
	}, [editProfileOutcome, toast, isMobileView])


	const resendVerificationEmail = () => {
		resendEmailVerification()
		setHideVerifyEmailPopover(true)
	}

	const translateTextSectionIndex = (text, isAdministrator) => {
		let translatedSectionIndex = 0;
		switch (text) {
			case ("set-brand-colors"):
			case ("manage-org"): {
				if (isAdministrator) {
					translatedSectionIndex = 1;
				}

				break;
			}
			default: {
				break;
			}
		}

		setDisplayedSectionIndex(translatedSectionIndex)
	}

	const preloadImages = () => {
		const { profilePicture, orgLogo } = localState;
		return (
			<Box className="image-loading__in-progress">
				{profilePicture && <Image src={profilePicture} />}
				{orgLogo && <Image src={orgLogo} />}
			</Box>
		)
	}

	const handleShowModal = () => {
		setShowEditProfileModal(true)
	}

	const handleCloseModal = () => {
		setShowEditProfileModal(false)
	}

	const handleLoadingNewTab = () => {
		isMobileView && setMobilePageLoading(true)
	}

	const { isAdministrator, firstName, lastName, pageIsLoading, profileIsEmpty, isNoOrgUser, orgName, profilePicture } = localState;
	const dataIsLoading = (!!pageIsLoading) ? true : false;


	if (dataIsLoading === true) {
		return (
			<>
				<PageLoader />
				{preloadImages()}
			</>
		)
	} else if (isMobileView && mobilePageLoading === true) {
		return (
			<Flex w='100%' minH='100vh' bg='white' align='center' justify='center' rounded='0.5em'>
				<Box mb='5em'>
					<LaunchdeckSpinner thickness='3px' boxSize='4em' />
				</Box>
			</Flex>
		)

	}

	if (dataIsLoading === false) {
		return (
			<Flex direction='column' align='flex-start' pb='3em' minHeight='40em' bg='white' rounded='0.25em' px={isMobileView && '1em'} >
				{isMobileView ?
					<UserProfileMobileNavMenu
						isAdministrator={isAdministrator}
						sectionIndex={displayedSectionIndex}
						handleLoadingNewTab={handleLoadingNewTab}
					/>
					:
					<Box w='100%'>
						<Box className="page-title" pt='1.1em' pb='0.5em' px='1em'>My Account</Box>
						<Divider borderColor='gray.300' opacity='50%' />
					</Box>
				}


				<Flex direction={['column', 'column', 'column', 'row']} justify='space-between' flexWrap='wrap' width='100%'>
					<Box mt={'2em'} w='100%' >
						<Tabs display={isMobileView ? '' : 'flex'} variant="unstyled" orientation={'vertical'} w='100%' index={displayedSectionIndex} defaultIndex={displayedSectionIndex}
							onChange={(index) => setDisplayedSectionIndex(index)}
						>
							<TabList bg='white.50' rounded='0.5em' flexWrap='wrap' justify='flex-start' visibility={isMobileView ? 'hidden' : ''} h={isMobileView ? '1px' : ''}>
								<Tab _selected={{ bg: 'gray.50', color: "brand.500", fontWeight: '600', borderRight: '1px solid', borderColor: 'brand.500' }} fontSize='1em'><Box py='0.8em' textAlign='left' w='100%' minW='12em' maxW='17.5em' px='1em'>Profile</Box></Tab>
								{/* <Tab _selected={{ bg: 'gray.50', color: "brand.500", fontWeight: '600', borderRight: '1px solid', borderColor: 'brand.500' }} fontSize='1em'><Box py='0.8em' textAlign='left' w='100%' minW='12em' maxW='17.5em' px='1em'>Preference</Box></Tab> */}
								{isAdministrator && <Tab _selected={{ bg: 'gray.50', color: "brand.500", fontWeight: '600', borderRight: '1px solid', borderColor: 'brand.500' }} fontSize='1em'><Box py='0.8em' textAlign='left' w='100%' minW='12em' maxW='17.5em' px='1em'>Organization</Box></Tab>}
								{isAdministrator && <Tab _selected={{ bg: 'gray.50', color: "brand.500", fontWeight: '600', borderRight: '1px solid', borderColor: 'brand.500' }} fontSize='1em'><Box py='0.8em' textAlign='left' w='100%' minW='12em' maxW='17.5em' px='1em'>Billing</Box></Tab>}
								<Tab _selected={{ bg: 'gray.50', color: "brand.500", fontWeight: '600', borderRight: '1px solid', borderColor: 'brand.500' }} fontSize='1em'><Box py='0.8em' textAlign='left' w='100%' minW='12em' maxW='17.5em' px='1em'>Integrations</Box></Tab>
								{/* <Tab _selected={{ bg:'gray.50', color: "brand.500", fontWeight:'600', borderRight:'1px solid', borderColor:'brand.500'}} fontSize='1em'><Box py='0.8em' textAlign='left' w='100%' minW='12em' maxW='17.5em' px='1em'>Email Settings</Box></Tab> */}
								<Tab _selected={{ bg: 'gray.50', color: "brand.500", fontWeight: '600', borderRight: '1px solid', borderColor: 'brand.500' }} fontSize='1em'><Box py='0.8em' textAlign='left' w='100%' minW='12em' maxW='17.5em' px='1em'>Settings</Box></Tab>

							</TabList>

							{!isMobileView && <Divider orientation="vertical" borderColor='gray.300' opacity='50%' mx='0' />}

							<TabPanels mx={['0', '2em', '2em', '2em']} w='100%' minH={isMobileView ? '' : '80vh'} px={['0em', '2em', '2em', '2em']}>
								<TabPanel>
									<PersonalInformationSection
										userProfilePicture={profilePicture}
										stateData={localState}
										userAccountProfile={userAccountProfile}
										resendVerificationEmail={resendVerificationEmail}
										handleShowModal={handleShowModal}
										handleCloseModal={handleCloseModal}
										showEditProfileModal={showEditProfileModal}
										isMobileView={isMobileView}
										hideVerifyEmailPopover={hideVerifyEmailPopover}
									/>
									<Box>
										{isNoOrgUser === true ?
											<OrgUpCtaModule /> :
											<OrganizationMemberInformationSection
												stateData={localState}
												isMobileView={isMobileView}
												isNoOrgUser={isNoOrgUser}
												translateTextSectionIndex={translateTextSectionIndex}
											/>
										}
									</Box>
								</TabPanel>

								{/* <TabPanel>
									<Box> Room Preference</Box>
									<Box> Preference</Box>
								</TabPanel> */}

								{isAdministrator &&
									<TabPanel>
										<Flex direction='column' w='100%'>
											<OrgManagementModule
												adminOrgProfile={adminOrgProfile}
												manageAdminOrgUsersOutcome={manageAdminOrgUsersOutcome}
												updateOrgProfile={updateOrgProfile}
												updateOrgProfileReset={updateOrgProfileReset}
												paidFeatureAccess={paidFeatureAccess}
											/>
										</Flex>
									</TabPanel>
								}

								{isAdministrator &&
									<TabPanel>
										<OrgBillingModule
											adminOrgProfile={adminOrgProfile}
											manageAdminOrgUsersOutcome={manageAdminOrgUsersOutcome}
											isMobileView={isMobileView}
										/>
									</TabPanel>
								}

								<TabPanel>
									<ExternalIntegrationsModule />
								</TabPanel>

								{/* <TabPanel>
										<Flex direction='column' w='100%'>
											<Box className="user-profile-section__first-label"> Email Settings </Box>
											<Box color='gray.500' maxWidth='500px'>Choose events you would like to be notified for. </Box>
											<UserEmailSettings />
										</Flex>
									</TabPanel> */}

								<TabPanel>
									<UserSettingsManagementModule
										isNoOrgUser={isNoOrgUser}
										firstName={firstName}
										lastName={lastName}
										isAdministrator={isAdministrator}
										orgName={orgName}
									/>
								</TabPanel>

							</TabPanels>
						</Tabs>

					</Box>
				</Flex>
				<LoggedOutConfirmationModal />
			</Flex>
		);
	}
	else if (profileIsEmpty === true) {
		return (
			<Redirect to="/setup-profile" />
		)
	}
	else {
		console.log('There is no profile state to load')
		return null;
	}
}


function PersonalInformationSection({ stateData, userAccountProfile, userProfilePicture, resendVerificationEmail, handleShowModal, handleCloseModal, showEditProfileModal, isMobileView, hideVerifyEmailPopover }) {
	const { fullName, firstName, lastName, email, meetingLink, contactPhoneNumber, isVerified, profilePicture } = stateData;
	const { contactPhoneNumber: updatedPhoneNumber } = userAccountProfile || {}
	const displayedContactNumber = updatedPhoneNumber ? updatedPhoneNumber : contactPhoneNumber
	const userProfile = { firstName, lastName, email, profilePicture };

	const [displayedProfilePic, setDisplayedProfilePic] = useState(null)
	const [clickCount, setClickCount] = useState(0)

	useEffect(() => {
		if (userProfilePicture) {
			setDisplayedProfilePic(userProfilePicture)
			setClickCount(prevCount => prevCount + 1)
		}
	}, [userProfilePicture])

	return (
		<Box>
			{!isMobileView &&
				<>
					<Flex w='100%' gap={8} justify='flex-start' alignItems={['flex-start', 'center', 'center', 'center']}					>
						<Box className="user-profile-section__label" letterSpacing='1px' fontSize='1.2em' > Personal Profile</Box>
						<Box color='brand.500' fontSize='0.875em' fontWeight='500' cursor='pointer' onClick={handleShowModal}>Edit</Box>
					</Flex>

					<Divider />
				</>
			}


			<Flex direction={isMobileView ? 'column' : 'row'} justify='flex-start' align='center' mt='2em' mb='1.5em'>
				<Box position='relative'>
					<UserProfileImageDisplay key={`user-profile-pic-${clickCount}`} name={fullName} size='5em' profilePicture={displayedProfilePic} />
				</Box>

				<Box ml={['0', '1.6em']} mt={['0.5em', '0']}>
					<Box fontSize='20px' lineHeight='30px' fontWeight='semibold' textAlign={isMobileView && 'center'}>{fullName && fullName}</Box>
					<Flex justify={isMobileView ? 'center' : 'flex-start'} align='center' w='100%'>
						<Box color='gray.500' fontSize='0.8em' textAlign={isMobileView && 'center'}>{email}</Box>
						{isVerified ? <VerifiedUserTag isVerified={isVerified} />
							: <Box as='span' ml='1em'>
								<Flex justify='flex-start' align='center'>
									<LaunchdeckInteractiveButton label='Verify email' endLabel='Verification email sent' onClick={resendVerificationEmail} />
									<Box ml='0.5em'>

										{!hideVerifyEmailPopover &&
											<Popover placement={isMobileView ? 'top' : 'right'}>
												<PopoverTrigger>
													<Icon cursor='pointer' mt={isMobileView && '0.5em'} />
												</PopoverTrigger>
												<PopoverContent w={isMobileView ? '20em' : '25em'} color='brandDark.500' bg='white' borderColor='brand.500' boxShadow='lg'>

													<PopoverHeader pt={4} fontWeight='bold' border='0' px='1em'>
														Why verify email?
													</PopoverHeader>
													<PopoverCloseButton top='1em' right='1em' fontSize='0.75em' />

													<PopoverBody fontSize='0.875em' my='0.875em' px='1em'>
														<Flex w='100%' px='2em' justify='center'>
															<Flex w='100%' my='1em' justify='flex-start' align='center' bg='gray.50' border='1px solid' borderColor='gray.300' rounded='0.25em' p='0.75em 2em'>
																<Avatar h='3.8em' w='3.8em' bg='gray.200' color='gray.700' src={""} />

																<Box ml='1em'>
																	<Flex align='center' gap={4} mb='0.2em'>
																		<Box height='0.8em' w='8em' rounded='0.5em' bg='gray.500' />
																		<Box as='span'>
																			<Box as={RiVerifiedBadgeFill} boxSize='1.6em' color='brand.500' />
																		</Box>
																	</Flex>
																	<Box height='0.8em' w='12em' rounded='0.5em' bg='gray.300' />
																</Box>
															</Flex>
														</Flex>

														<Box mt='1.5em'>
															Get a trusted blue tick & let your stakeholders know they are dealing with the real person
														</Box>

													</PopoverBody>
													<PopoverFooter
														border='0'
														display='flex'
														alignItems='center'
														justifyContent='space-between'
														pb={4}
													>

														<Flex pl='0.5em' fontSize='0.7em' fontWeight='600' color='brandPink.500' gap={2} justify='center' align='center'>
															<MdOutlineTipsAndUpdates />
															Launchdeck Tips</Flex>

													</PopoverFooter>
												</PopoverContent>
											</Popover>
										}
									</Box>
								</Flex>
							</Box>
						}
					</Flex>

				</Box>

			</Flex>


			<Box mt='3em' mb='5em' >
				<Flex mt='1.5em' justify='space-apart' align='flex-start' direction={['column', 'row', 'row', 'row']}>
					<Box flex='1 1 0' w='100%'>
						<Flex justify={isMobileView ? 'center' : 'flex-start'} align='center'>
							<Box className="user-profile-section__sublabel">Contact Number</Box>
						</Flex>

						<Box color='brandDark.500' fontSize='0.875em' lineHeight='22px' mt='0.5em' textAlign={isMobileView && 'center'}>{displayedContactNumber ? displayedContactNumber : "-"}</Box>
					</Box>
				</Flex>


				<Flex mt='1.5em' justify='space-apart' align='flex-start' direction={['column', 'row', 'row', 'row']}>
					<Box flex='1 1 0' w='100%'>
						<Flex justify={isMobileView ? 'center' : 'flex-start'} align='center'>
							<Box className="user-profile-section__sublabel">Book-a-meeting link</Box>
						</Flex>
						<Box color='brandDark.500' fontSize='0.875em' lineHeight='22px' mt='0.5em' textAlign={isMobileView && 'center'} className='truncate-long-text'>{meetingLink ? meetingLink : "-"}</Box>
					</Box>

				</Flex>

				{isMobileView &&
					<Flex justify='center' w='100%' mt='2em'>
						<LaunchdeckButton bg='brand.500' color='white' w='100%' onClick={handleShowModal} label='Edit' />
					</Flex>
				}
			</Box>

			<LaunchdeckModal
				title='Edit Personal Info'
				handleCloseModal={handleCloseModal}
				showModal={showEditProfileModal}
				maxW='35em'
			>
				<UserEditPersonalInfoForm userProfile={userProfile} handleCloseModal={handleCloseModal} />
			</LaunchdeckModal>
		</Box>
	)
}

function OrganizationMemberInformationSection({ stateData, isMobileView, isNoOrgUser, translateTextSectionIndex }) {
	const { orgName, orgLogo, userOrgTitle, orgBranding, isAdministrator } = stateData;
	const { primaryColor, secondaryColor } = orgBranding || {};
	const displayedBrandColorCopy = isAdministrator ? `Set ${orgName}'s brand colors for your team` : `Request ${orgName}'s administrators to provide them for your team`

	return (
		<Box mt='2em'>

			<Flex w='100%' gap={8} justify='flex-start' alignItems={['flex-start', 'center', 'center', 'center']}			>
				<Flex w='100%' justify='flex-start' align={['', 'center', 'center', 'center']} direction={['column', 'row', 'row', 'row']}>
					<Box className="user-profile-section__first-label" letterSpacing='1px' fontSize='1.2em' textAlign={isMobileView && 'center'}> Your Organization</Box>

					<Box ml='1em' display={isMobileView ? "none" : "block"}>
						{!isNoOrgUser && <LaunchdeckTooltip hoverMessage={`This section is managed by your organization's Admininstrator(s)`} />}
					</Box>
				</Flex>
			</Flex>

			<Divider mb='1em' />

			{orgLogo && <Image maxH={'5em'} rounded='0.25em' src={orgLogo} mt='1em' mb='2em' />}

			<Flex justify='space-apart' align='flex-start' direction={['column', 'row', 'row', 'row']}>
				<Box flex='1 1 0' justify={isMobileView ? "center" : 'flex-start'} w='100%'>
					<Box className="user-profile-section__sublabel" textAlign={isMobileView && 'center'}>Name</Box>
					<Box color='brandDark.500' fontSize='1em' lineHeight='22px' mt='0.5em' textAlign={isMobileView && 'center'}>{orgName}</Box>
				</Box>
			</Flex>

			<Box pt='2em'>
				<Box className="user-profile-section__sublabel" textAlign={isMobileView && 'center'}>Position</Box>
				<Box color={'brandDark.500'} fontSize='1em' lineHeight='22px' mt='0.5em' textAlign={isMobileView && 'center'}>{userOrgTitle || '-'}</Box>
			</Box>

			<Box pt='2em'>
				<Box className="user-profile-section__sublabel" textAlign={isMobileView && 'center'}>Brand colors</Box>
				<Flex mt='0.5em' justify={isMobileView ? "center" : "flex-start"}>
					{orgBranding ?
						<Flex gap={'0.5em'}>
							<Box boxSize='2em' bg={primaryColor || 'gray.200'} border='1px solid' borderColor='gray.100' rounded='0.5em' />
							<Box boxSize='2em' bg={secondaryColor || 'gray.200'} border='1px solid' borderColor='gray.100' rounded='0.5em' />
						</Flex>
						:
						<Flex fontSize='0.8em' color={'brandDark.500'} gap='0.25em' textAlign={isMobileView && "center"}>{displayedBrandColorCopy} {isAdministrator && <Box color='brand.300' as='span' onClick={() => translateTextSectionIndex('manage-org', isAdministrator)} cursor='pointer'>here</Box>}</Flex>
					}
				</Flex>
			</Box>
		</Box>
	)
}
